import { useEffect, useState } from 'react';
import { Save, RotateCcw, LayoutTemplate, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, useConfirm } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { TextField, TextArea, ColorField } from '@/components/shared/CmsFields';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('home');

interface PromoBannerConfig {
  enabled: boolean;
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
  bgFrom: string;
  bgTo: string;
  textColor: string;
  ctaBgColor: string;
  ctaTextColor: string;
}
interface SectionHeadingConfig {
  enabled: boolean;
  eyebrow: string;
  title: string;
  description: string;
  bgColor: string;
  textColor: string;
}
interface HomeContent {
  promoBanner: PromoBannerConfig;
  featuredProducts: SectionHeadingConfig;
  newArrivals: SectionHeadingConfig;
  categories: SectionHeadingConfig;
}

const DEFAULTS: HomeContent = {
  promoBanner: {
    enabled: true,
    eyebrow: 'Limited time offer',
    title: 'Dashain & Tihar Sale',
    description: 'Up to 50% off on selected products. Celebrate the festivals with the best deals!',
    ctaLabel: 'Shop the sale',
    ctaHref: '/products?filter=on-sale',
    bgFrom: '#C2410C',
    bgTo: '#FEF3C7',
    textColor: '#FFFFFF',
    ctaBgColor: '#FFFFFF',
    ctaTextColor: '#9A3412',
  },
  categories: {
    enabled: true, eyebrow: 'Shop', title: 'Browse by category',
    description: 'Find what you need across 40+ categories from verified vendors.',
    bgColor: '#EBEAE1', textColor: '#0A1D04',
  },
  newArrivals: {
    enabled: true, eyebrow: 'New & noteworthy', title: 'Just landed',
    description: 'Fresh products added this week from sellers across Nepal.',
    bgColor: '#EBEAE1', textColor: '#0A1D04',
  },
  featuredProducts: {
    enabled: true, eyebrow: 'Shop everything', title: 'All products',
    description: 'Browse the full Shishyastra catalog — fresh picks from every verified store.',
    bgColor: '#EBEAE1', textColor: '#0A1D04',
  },
};

function SectionForm({ label, value, onChange }: { label: string; value: SectionHeadingConfig; onChange: (next: SectionHeadingConfig) => void }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          {label}
          <Badge variant={value.enabled ? 'success' : 'secondary'}>{value.enabled ? 'Visible' : 'Hidden'}</Badge>
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => onChange({ ...value, enabled: !value.enabled })}>
          {value.enabled ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Show</>}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Eyebrow" value={value.eyebrow} onChange={(v) => onChange({ ...value, eyebrow: v })} />
          <TextField label="Title" value={value.title} onChange={(v) => onChange({ ...value, title: v })} />
        </div>
        <TextArea label="Description" value={value.description} onChange={(v) => onChange({ ...value, description: v })} />
        <div className="grid sm:grid-cols-2 gap-3">
          <ColorField label="Section background" value={value.bgColor} onChange={(v) => onChange({ ...value, bgColor: v })} />
          <ColorField label="Text color" value={value.textColor} onChange={(v) => onChange({ ...value, textColor: v })} />
        </div>
      </CardContent>
    </Card>
  );
}

export function HomepageCms() {
  const confirm = useConfirm();
  const [content, setContent] = useState<HomeContent>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          setContent({ ...DEFAULTS, ...data });
        }
      } catch {
        toast.error('Could not reach storefront CMS — is the storefront running?');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(content) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Homepage saved', { description: 'Refresh the storefront to see changes.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!(await confirm({
      title: 'Reset all homepage content to defaults?',
      description: 'Your current section text and colors will be replaced. Nothing is published until you click Save.',
      confirmLabel: 'Reset',
      tone: 'danger',
    }))) return;
    setContent(DEFAULTS);
    toast.info('Reset to defaults — click Save to publish.');
  };

  const banner = content.promoBanner;
  const set = (key: keyof HomeContent, val: SectionHeadingConfig) => setContent((c) => ({ ...c, [key]: val }));
  const setBanner = (patch: Partial<PromoBannerConfig>) => setContent((c) => ({ ...c, promoBanner: { ...c.promoBanner, ...patch } }));

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Homepage CMS"
        description="Edit promotional banners, section headings, colors, and CTAs on the storefront homepage."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}><RotateCcw className="h-4 w-4" /> Reset</Button>
            <Button asChild variant="outline" size="sm">
              <a href={STOREFRONT_URL} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> Open storefront</a>
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Publish changes'}
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base inline-flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-primary-600" />
            Promo banner
            <Badge variant={banner.enabled ? 'success' : 'secondary'}>{banner.enabled ? 'Visible' : 'Hidden'}</Badge>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => setBanner({ enabled: !banner.enabled })}>
            {banner.enabled ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Show</>}
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="relative rounded-2xl p-8 overflow-hidden"
            style={{ background: `linear-gradient(90deg, ${banner.bgFrom}, ${banner.bgTo})`, color: banner.textColor }}>
            <p className="text-[11px] font-bold uppercase tracking-widest opacity-90">{banner.eyebrow}</p>
            <h3 className="font-bold text-3xl mt-1.5">{banner.title}</h3>
            <p className="opacity-90 mt-2 max-w-xl">{banner.description}</p>
            <button type="button" className="mt-5 px-6 py-2.5 rounded-full font-bold shadow-md"
              style={{ backgroundColor: banner.ctaBgColor, color: banner.ctaTextColor }}>
              {banner.ctaLabel}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Eyebrow" value={banner.eyebrow} onChange={(v) => setBanner({ eyebrow: v })} />
            <TextField label="Title" value={banner.title} onChange={(v) => setBanner({ title: v })} />
          </div>
          <TextArea label="Description" value={banner.description} onChange={(v) => setBanner({ description: v })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="CTA label" value={banner.ctaLabel} onChange={(v) => setBanner({ ctaLabel: v })} />
            <TextField label="CTA URL" value={banner.ctaHref} onChange={(v) => setBanner({ ctaHref: v })} placeholder="/products?filter=on-sale" />
          </div>
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Colors</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <ColorField label="BG start" value={banner.bgFrom} onChange={(v) => setBanner({ bgFrom: v })} />
              <ColorField label="BG end" value={banner.bgTo} onChange={(v) => setBanner({ bgTo: v })} />
              <ColorField label="Text" value={banner.textColor} onChange={(v) => setBanner({ textColor: v })} />
              <ColorField label="CTA bg" value={banner.ctaBgColor} onChange={(v) => setBanner({ ctaBgColor: v })} />
              <ColorField label="CTA text" value={banner.ctaTextColor} onChange={(v) => setBanner({ ctaTextColor: v })} />
            </div>
          </div>
        </CardContent>
      </Card>

      <SectionForm label="Categories section" value={content.categories} onChange={(v) => set('categories', v)} />
      <SectionForm label="New arrivals section" value={content.newArrivals} onChange={(v) => set('newArrivals', v)} />
      <SectionForm label="Featured products section" value={content.featuredProducts} onChange={(v) => set('featuredProducts', v)} />
    </div>
  );
}
